import type { Action } from '@/components/ButtonGroupActions'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createNumberFormatter,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { QUERY_KEY_COMBINATIONS, QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import AddIcons from '@/icons/AddIcons'
import ChevronDownIcons from '@/icons/ChevronDownIcons'
import MoneyIcons from '@/icons/MoneyIcons'
import ReleaseNoteIcons from '@/icons/ReleaseNoteIcons'
import ServicePowerIcons from '@/icons/ServicePowerIcons'
import SettingIcons from '@/icons/SettingIcons'
import TimeIcons from '@/icons/TimeIcons'
import { previewVatItems } from '@/modules/Invoice/services'
import type { PreviewVatItem } from '@/modules/Invoice/types'
import {
    ServiceCreatableType,
    ServiceSupportRegisters,
    ServiceSupportRenews,
    ServiceSupportStatements,
} from '@/modules/Service/constants'
import {
    createManualInvoice,
    createStatementInvoice,
    registerService,
    renewService,
} from '@/modules/Service/services'
import type { Service } from '@/modules/Service/types'
import { ServiceStatus } from '@/modules/Service/types'
import { formatCurrency, formatDateWithFormatter } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'

interface UseServiceActionsParams {
    service: Service | undefined
    serviceID: string | undefined
    showMoreDetails: boolean
    setShowMoreDetails: (value: boolean) => void
}

/**
 * Encapsulates all service detail action buttons logic:
 * mutations, handlers, visibility/disabled rules, and the actions array.
 */
export const useServiceActions = ({
    service,
    serviceID,
    showMoreDetails,
    setShowMoreDetails,
}: UseServiceActionsParams) => {
    const { t } = useTranslation('services')
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const [loadingPreview, setLoadingPreview] = useState(false)

    // ── Query refetch callbacks ──────────────────────────────────────────
    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [
            [QUERY_KEYS.SERVICE_DETAIL, Number(serviceID)].join('.'),
            `services.${serviceID}.actions`,
        ],
    })

    const { refetch: refetchInvoices } = useQueryClientCallback({
        reactQueryKeys: ['.', QUERY_KEYS.TICKETS],
    })

    // ── Mutations ────────────────────────────────────────────────────────
    const { mutateAsync: registerServiceMutate, isPending: isRegisterPending } =
        useMutationWithHandler(
            { mutationFn: registerService },
            {
                successMessage: t('register-service-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    refetch()
                },
            }
        )

    const { mutateAsync: renewServiceMutate, isPending: isRenewPending } =
        useMutationWithHandler(
            { mutationFn: renewService },
            {
                successMessage: t('renew-service-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    refetch()
                },
            }
        )

    const {
        mutateAsync: createManualInvoiceMutate,
        isPending: isCreateManualInvoicePending,
    } = useMutationWithHandler(
        { mutationFn: createManualInvoice },
        {
            successMessage: t('create-service-invoice-renew-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_INVOICE,
            onSuccess: () => {
                refetchInvoices()
            },
        }
    )

    const { mutateAsync: createStatementInvoiceMutate } =
        useMutationWithHandler(
            { mutationFn: createStatementInvoice },
            {
                successMessage: t('create-service-invoice-statement-success'),
                reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_INVOICE,
                onSuccess: () => {
                    refetchInvoices()
                },
            }
        )

    // ── Derived state ────────────────────────────────────────────────────
    const isInactiveService = [
        ServiceStatus.PendingTerminated,
        ServiceStatus.Rejected,
        ServiceStatus.Cancelled,
    ].includes(service?.service_status as ServiceStatus)

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleRegister = () => {
        setConfirmation({
            open: true,
            description: t('service-register', {
                service_name: `${service?.product_group_name} - ${service?.service_name}`,
                portal_name:
                    service?.name ??
                    [
                        service?.customer_id,
                        service?.company_name || service?.customer_name,
                    ]
                        .join(' - ')
                        ?.toUpperCase(),
            }),
            title: t('register-service'),
            onConfirm: () =>
                registerServiceMutate(service?.service_id as number),
        })
    }

    const handleRenew = () => {
        setConfirmation({
            open: true,
            description: t('renew-service', {
                service_name: `${service?.product_group_name} - ${service?.service_name}`,
            }),
            title: t('renew-service'),
            onConfirm: () => renewServiceMutate(service?.service_id as number),
        })
    }

    // ── Invoice renew with VAT preview ───────────────────────────────────
    const previewColumns: DataTableColumn<PreviewVatItem>[] = [
        {
            key: 'name',
            title: t('invoice-item-name'),
            width: 160,
            cellClassName: 'uppercase',
        },
        {
            key: 'start_date',
            title: t('start-date'),
            width: 110,
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
            cellClassName: 'text-right',
        },
        {
            key: 'end_date',
            title: t('end-date'),
            width: 110,
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
            cellClassName: 'text-right',
        },
        {
            key: 'usage_quantity',
            title: t('quantity'),
            width: 80,
            formatter: createNumberFormatter({ decimals: 0 }),
        },
        {
            key: 'amount',
            title: t('amount'),
            width: 120,
            formatter: createCurrencyFormatter({ currency: '' }),
        },
        {
            key: 'tax_percentage',
            title: t('vat'),
            width: 70,
            valueGetter: (row) => row.tax_percentage * 100,
            formatter: createNumberFormatter({ decimals: 2, suffix: '%' }),
        },
    ]

    const buildPreviewContent = (items: PreviewVatItem[]) => {
        if (!items.length) return undefined
        const totalAmount = items.reduce(
            (sum, item) => sum + (item.amount || 0),
            0
        )
        return (
            <div className="border border-gray-200 rounded-md overflow-hidden">
                <Typogaphy className="text-primary mb-2 font-medium">
                    {t('invoice-renew-preview')}
                </Typogaphy>
                <DataTableCustomized<PreviewVatItem>
                    columns={previewColumns}
                    rows={items}
                    size="small"
                    rowKey={(_row, index) => `preview-vat-${index}`}
                    emptyMessage={t('no-data')}
                />
                <div className="bg-indigo-50 px-4 py-2.5 border-t-2 border-indigo-200">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-primary capitalize">
                            {t('total')}
                        </span>
                        <span className="text-base font-bold text-primary capitalize">
                            {formatCurrency(totalAmount)}
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    const buildInvoiceRenewConfirmation = (
        customContent?: React.ReactNode
    ) => ({
        open: true,
        title: t('create-service-invoice-renew'),
        description: t('create-service-invoice-renew-description', {
            service_name: `${service?.product_group_name} - ${service?.service_name}`,
            continue_startdate: service?.continue_startdate
                ? formatDateWithFormatter(service?.continue_startdate)
                : '',
            continue_enddate: service?.continue_enddate
                ? formatDateWithFormatter(service?.continue_enddate)
                : '',
        }),
        customContent,
        onConfirm: () =>
            createManualInvoiceMutate({
                serviceID: service?.service_id as number,
                reason: '',
                action: 'renew',
            }),
    })

    const handleCreateInvoiceRenew = async () => {
        setLoadingPreview(true)
        try {
            const res = await previewVatItems({
                gte: service?.continue_startdate || '',
                lte: service?.continue_enddate || '',
                price: Number(service?.service_price_recurring) || 0,
                product_group_name: service?.product_group_name || '',
                service_id: service?.service_id,
            })
            const items: PreviewVatItem[] = res?.results || []
            setConfirmation(
                buildInvoiceRenewConfirmation(buildPreviewContent(items))
            )
        } catch {
            setConfirmation(buildInvoiceRenewConfirmation())
        } finally {
            setLoadingPreview(false)
        }
    }

    // ── Actions array ────────────────────────────────────────────────────
    const serviceActions: Partial<Action & { hidden?: boolean }>[] = [
        {
            label: t('create-service-note'),
            onClick: () => {
                setModal('ServiceModals/CreateServiceNoteModal/index', {
                    serviceID: service?.service_id,
                })
            },
            variant: 'outlined',
            icon: <ReleaseNoteIcons />,
        },
        {
            label: t('service-edit'),
            onClick: () => {
                setModal('ServiceModals/EditServiceModal/index', {
                    serviceID: service?.service_id,
                    service,
                })
            },
            disabled: isInactiveService,
            variant: 'outlined',
            icon: <TimeIcons />,
        },
        {
            label: t('register-service'),
            onClick: handleRegister,
            disabled:
                isInactiveService ||
                !!service?.service_creatable_uuid ||
                !ServiceSupportRegisters.includes(
                    service?.service_template as ServiceCreatableType
                ) ||
                isRegisterPending,
            hidden:
                !!service?.service_creatable_uuid ||
                !ServiceSupportRegisters.includes(
                    service?.service_template as ServiceCreatableType
                ) ||
                isRegisterPending,
            variant: 'contained',
            color: 'success',
            icon: <ServicePowerIcons />,
        },
        {
            label: t('renew-service'),
            onClick: handleRenew,
            disabled:
                isInactiveService ||
                !service?.service_creatable_uuid ||
                !ServiceSupportRenews.includes(
                    service?.service_template as ServiceCreatableType
                ) ||
                isRenewPending,
            hidden: !ServiceSupportRenews.includes(
                service?.service_template as ServiceCreatableType
            ),
            variant: 'contained',
            color: 'success',
            icon: <ServicePowerIcons />,
        },
        {
            label: t('service-create-invoice-renew'),
            onClick: handleCreateInvoiceRenew,
            disabled:
                service?.is_poc ||
                isInactiveService ||
                loadingPreview ||
                isCreateManualInvoicePending ||
                ![
                    ServiceStatus.Active,
                    ServiceStatus.WaitingCreateService,
                ].includes(service?.service_status as ServiceStatus) ||
                service?.service_template === 'Device.Liquidation' ||
                service?.service_template === 'Server.Dedicated',
            variant: 'contained',
            color: 'success',
            icon: <MoneyIcons />,
        },
        {
            label: t('service-create-invoice-statement'),
            onClick() {
                setConfirmation({
                    open: true,
                    description: t(
                        'service-create-invoice-statement-description',
                        {
                            service_name: `${service?.product_group_name} - ${service?.service_name}`,
                        }
                    ),
                    title: t('service-create-invoice-statement'),
                    onConfirm: () =>
                        createStatementInvoiceMutate({
                            serviceID: service?.service_id as number,
                            reason: '',
                        }),
                })
            },
            disabled: isInactiveService,
            hidden:
                service?.service_status !== ServiceStatus.Active ||
                !ServiceSupportStatements.includes(
                    service?.service_template as ServiceCreatableType
                ),
            variant: 'contained',
            color: 'primary',
            icon: <MoneyIcons />,
        },
        {
            label: t('service-create-invoice-fee'),
            onClick() {
                setModal('ServiceModals/CreateFeeInvoiceModal/index', {
                    serviceID: service?.service_id,
                    service,
                })
            },
            disabled: isInactiveService,
            // Trialing POCs bill through fee invoices (renewal is disabled for POC)
            hidden: !(
                service?.service_status === ServiceStatus.Active ||
                (service?.is_poc &&
                    service?.service_status === ServiceStatus.Trialing)
            ),
            variant: 'contained',
            color: 'primary',
            icon: <MoneyIcons />,
        },
        {
            label: t('cancel-service'),
            onClick() {
                setModal('ServiceModals/CancelledServiceModal/index', {
                    service,
                })
            },
            disabled:
                isInactiveService ||
                service?.service_status === ServiceStatus.WaitingConfirm ||
                service?.service_template === 'Device.Liquidation',
            variant: 'contained',
            color: 'error',
            icon: <ServicePowerIcons />,
        },
        {
            label: t('addons-service'),
            disabled: isInactiveService || service?.is_poc,
            onClick() {
                const params = new URLSearchParams({
                    service_id: String(service?.service_id),
                })
                navigate(
                    generatePathDatacentral(paths.serviceCreateAddons, {
                        id: service?.service_id,
                    }) +
                        '?' +
                        params.toString()
                )
            },
            hidden: [
                'SSL.SSL',
                'Domain.VNNIC',
                'Domain.TLDs',
                'Device.Liquidation',
            ].includes(service?.service_template || ''),
            variant: 'outlined',
            color: 'primary',
            icon: <AddIcons />,
        },
        {
            label: t('edit-service-config'),
            onClick() {
                navigate(
                    generatePathDatacentral(paths.serviceDetail, {
                        id: serviceID,
                    }) + '?tab=service-config'
                )
            },
            // Page opens for any active service to manage its config + addons;
            // per-service editing is gated to waiting-confirm inside the form + API.
            disabled: isInactiveService,
            hidden: [
                'SSL.SSL',
                'Domain.VNNIC',
                'Domain.TLDs',
                'Device.Liquidation',
            ].includes(service?.service_template || ''),
            variant: 'contained',
            color: 'primary',
            icon: <SettingIcons />,
        },
        {
            label: t('show-more-details'),
            onClick() {
                setShowMoreDetails(!showMoreDetails)
            },
            variant: 'outlined',
            color: 'primary',
            icon: (
                <ChevronDownIcons
                    className={showMoreDetails ? 'rotate-180 transform' : ''}
                />
            ),
        },
    ]

    return { serviceActions, refetch }
}
